import classNames from 'classnames'
import { FC, useRef, useState } from 'react'
import useOutsideClick from 'src/hooks/useOutsideClick'
import styles from './entitySelect.module.scss'
import { firstLetter } from '../utils'

export interface EntitySelectOption {
  id: string
  label: string
}

interface EntitySelectProps {
  activeId?: string
  ariaLabel: string
  onSelect: (id: string) => void
  options: EntitySelectOption[]
}

// A lettered-avatar dropdown for switching between a few named profiles
// (competitors, brand profiles).
export const EntitySelect: FC<EntitySelectProps> = ({
  activeId,
  ariaLabel,
  onSelect,
  options,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useOutsideClick({ onOutsideClick: () => setIsOpen(false), ref })

  const active = options.find((option) => option.id === activeId)

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        className={styles.button}
        onClick={() => setIsOpen((open) => !open)}
        type="button"
      >
        <span className={styles.avatar}>{firstLetter(active?.label)}</span>
        <span className={styles.label}>{active?.label ?? 'Select'}</span>
        <svg
          className={classNames(styles.chevron, {
            [styles.chevronOpen]: isOpen,
          })}
          fill="none"
          height="18"
          viewBox="0 0 20 20"
          width="18"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      </button>
      {isOpen && (
        <div className={styles.menu} role="listbox">
          {options.map((option) => (
            <button
              aria-selected={option.id === activeId}
              className={classNames(styles.item, {
                [styles.itemActive]: option.id === activeId,
              })}
              key={option.id}
              onClick={() => {
                setIsOpen(false)
                onSelect(option.id)
              }}
              role="option"
              type="button"
            >
              <span className={styles.avatar}>{firstLetter(option.label)}</span>
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
